import React, { useState } from 'react';
import { authApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { LEGAL_VERSION } from '../legal/2026-10-29-v2';

const SignupForm = ({ paid }) => {
  const auth = useAuth();
  const [mode, setMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const changeMode = (next) => { setMode(next); setError(''); setNotice(''); setAcceptedTerms(false); };
  const signIn = async () => {
    const result = await auth.login(email.trim().toLowerCase(), password);
    if (result.new_password_required || result.mfa_required || result.mfa_setup_required) {
      throw new Error('This account requires an additional sign-in step. Please use the sign-in page, then return to Plans.');
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (mode === 'signup' && !acceptedTerms) {
      setError('Please read and agree to the Terms and Conditions before creating an account.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const address = email.trim().toLowerCase();
      if (mode === 'signup') {
        await authApi.signup(address, password, acceptedTerms);
        setMode('verify');
        setNotice('Check your email for a verification code.');
      } else if (mode === 'verify') {
        await authApi.confirmSignup(address, code.trim());
        // Confirmation is complete even if the following sign-in fails.
        setMode('login');
        setNotice('Email verified. Sign in to continue.');
        await signIn();
      } else {
        await signIn();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const resend = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await authApi.resendSignup(email.trim().toLowerCase());
      setNotice('A new verification code has been requested. Check your email.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <p>{mode === 'login' ? 'Sign in to continue.' : mode === 'verify' ? 'Verify your email to finish creating your account.' : paid
        ? 'Create your account first, then continue to secure payment. Your account starts on Free; choosing a paid tier does not grant paid access before payment.'
        : 'Create a free account. No payment card required.'}</p>
      <form className="login-form" onSubmit={submit}>
        <label htmlFor="signup-email">Email</label>
        <input id="signup-email" type="email" autoComplete="username" required maxLength={254} value={email} disabled={busy} onChange={(event) => setEmail(event.target.value)} />
        <label htmlFor="signup-password">Password</label>
        <input id="signup-password" type="password" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} required minLength={mode === 'login' ? 1 : 12} maxLength={256} value={password} disabled={busy} onChange={(event) => setPassword(event.target.value)} />
        {mode === 'signup' && <small>At least 12 characters, including uppercase, lowercase, a number, and a symbol.</small>}
        {mode === 'signup' && <label className="signup-terms" htmlFor="signup-terms">
          <input id="signup-terms" type="checkbox" required checked={acceptedTerms} disabled={busy} onChange={(event) => setAcceptedTerms(event.target.checked)} />
          <span>I have read and agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>, including the <a href="/disclaimer" target="_blank" rel="noopener noreferrer">Disclaimer</a>. Version {LEGAL_VERSION}. Links open in a new tab.</span>
        </label>}
        {mode === 'verify' && <>
          <label htmlFor="signup-code">Email verification code</label>
          <input id="signup-code" autoComplete="one-time-code" required value={code} disabled={busy} onChange={(event) => setCode(event.target.value)} />
        </>}
        {error && <div className="error-box" role="alert">{error}</div>}
        {notice && <p role="status">{notice}</p>}
        <button className="primary-button" disabled={busy || (mode === 'signup' && !acceptedTerms)} type="submit">{busy ? 'Please wait...' : mode === 'signup' ? 'Create account' : mode === 'verify' ? 'Verify email and continue' : 'Sign in and continue'}</button>
        {mode === 'verify' && <button className="text-action" type="button" disabled={busy || !email.trim()} onClick={resend}>Send another code</button>}
        {mode !== 'login' && <button className="text-action" type="button" disabled={busy} onClick={() => changeMode('login')}>Already have an account? Sign in</button>}
        {mode !== 'signup' && <button className="text-action" type="button" disabled={busy} onClick={() => changeMode('signup')}>Create a new account</button>}
        {mode !== 'verify' && <button className="text-action" type="button" disabled={busy} onClick={() => changeMode('verify')}>Already registered? Enter verification code</button>}
        <a href="/login">Open sign-in page or reset password</a>
      </form>
    </div>
  );
};

export default SignupForm;
